// Truck Maintenance — Unified Bucket Landing Page.
//
// One component renders every maintenance bucket using a config object.
// Mirrors the Discount/RequestBucketPage shape word-for-word so users
// switch between Sales and Maintenance buckets without re-learning the UI.
//
// The 5 buckets:
//   1. work_orders  → /maintenance/work-orders
//   2. incidents    → /maintenance/incidents
//   3. accidents    → /maintenance/accidents
//   4. inspections  → /maintenance/inspections
//   5. preventive   → /maintenance/preventive
//
// Each config supplies:
//   - title / icon
//   - listEndpoint   (server route returning {data, pagination})
//   - statsEndpoint  (returns {scope_kind, ...kpiKeys})
//   - exportEndpoint
//   - lockedFilters  (e.g. accident bucket pins incident_type='accident')
//   - statusOptions  (for the Status dropdown)
//   - filters        (which standard filter inputs to render)
//   - columns        (table columns + per-row renderer)
//   - kpis           (4 KPI cards keyed by stats payload)
//   - submitTo       (route for the "+ Submit new" button, OR null)
//   - SubmitModal    (component — opens in place instead of navigating;
//                     takes precedence over submitTo)
//   - submitLabel
//   - rowHref        (where clicking a row navigates)
//
// Locked rules honored:
//   * 4 timeframe presets, This Month default
//   * Auto-run on every filter change (no Search button)
//   * Free-text debounced 300ms
//   * 4 KPI cards always reflect the active filter set
//   * Export Excel respects the active filter set
//   * Gold primary CTAs, blue active preset, scope badge

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { api } from '../../api';

const SCOPE_PALETTES = {
  all:     { bg: '#0f172a',    fg: '#fff',    label: 'Scope: Company-wide' },
  reports: { bg: '#8b5cf615',  fg: '#8b5cf6', label: 'Scope: My Team' },
  own:     { bg: '#3b82f615',  fg: '#3b82f6', label: 'Scope: My Submissions' },
};

// Central-time date helpers (verbatim from RequestBucketPage).
function dateToCT(date) {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(date);
  return `${parts.find(p => p.type === 'year').value}-${parts.find(p => p.type === 'month').value}-${parts.find(p => p.type === 'day').value}`;
}
const getTodayCT = () => dateToCT(new Date());
const getLast7DaysCT = () => dateToCT(new Date(Date.now() - 7 * 86400000));
function getThisMonthStartCT() {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Chicago', year: 'numeric', month: '2-digit',
  }).formatToParts(new Date());
  return `${parts.find(p => p.type === 'year').value}-${parts.find(p => p.type === 'month').value}-01`;
}

function formatMoney(v) {
  if (v == null || v === '') return '$0.00';
  return `$${Number(v).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function fmtDate(s) {
  if (!s) return '—';
  // Server inspections return YYYY-MM-DD (shift_date) for inspections;
  // WOs/incidents return ISO timestamps. Handle both gracefully.
  const d = s.length === 10
    ? new Date(s + 'T12:00:00Z')
    : new Date(s.replace(' ', 'T') + (s.includes('Z') || s.includes('+') ? '' : 'Z'));
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'America/Chicago' });
}

export default function MaintenanceBucketPage({ config }) {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [searchParams] = useSearchParams();

  // Some buckets share the same underlying endpoint but lock on a filter
  // (Incidents and Accidents both hit /maintenance-history/incidents — the
  // config decides which). The `?type=` URL param can also override the
  // lock if a deep link wants to.
  //
  // CRITICAL: lockedFilters MUST be memoized. If it's recreated on every
  // render, buildQuery becomes a new function each render and the data
  // useEffect below fires endlessly (300ms-debounced fetch loop). That bug
  // hammered the apiLimiter (200/min) and locked users out of /api/auth/login.
  const urlTypeOverride = searchParams.get('incident_type');
  const lockedFilters = useMemo(() => ({
    ...(config.lockedFilters || {}),
    ...(urlTypeOverride ? { incident_type: urlTypeOverride } : {}),
  }), [config.lockedFilters, urlTypeOverride]);

  // Filter state — superset of all bucket filter shapes. Each bucket only
  // renders the inputs it cares about, but the param keys map directly
  // to the server query strings.
  const [activePreset, setActivePreset] = useState('month');
  const [filters, setFilters] = useState({
    truck_id: '',
    status: '',
    severity: '',
    priority: '',
    assigned_to: '',
    driver_id: '',
    reported_by: '',
    search: '',
    date_from: getThisMonthStartCT(),
    date_to: getTodayCT(),
  });

  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [trucks, setTrucks] = useState([]);
  const [activeUsers, setActiveUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState('');
  const [showSubmitModal, setShowSubmitModal] = useState(false);
  // Bumped after an in-place submit so the list + KPIs refetch.
  const [reloadKey, setReloadKey] = useState(0);

  // Load lookup data once. We keep these lean — only what the filter row
  // needs to render dropdown options.
  useEffect(() => {
    api.getTrucks().then(t => setTrucks(Array.isArray(t) ? t : (t?.trucks || []))).catch(() => {});
    api.getActiveUsers?.().then(setActiveUsers).catch(() => {});
  }, []);

  // Build the server query — strip empties so URLSearchParams doesn't
  // poison the WHERE clause with literal "undefined" strings.
  const buildQuery = useCallback(() => {
    const q = { ...filters, ...lockedFilters };
    Object.keys(q).forEach(k => { if (q[k] === '' || q[k] == null) delete q[k]; });
    return q;
  }, [filters, lockedFilters]);

  // Re-fetch on any filter change. Debounce ONLY when the free-text
  // search field changed — every other interaction (preset clicks,
  // dropdown changes) fetches immediately so the UI feels snappy.
  // Without this split, every preset click waits ~300ms before showing
  // updated data, which reads as "glitchy."
  const lastSearchRef = useRef(filters.search);
  useEffect(() => {
    const searchChanged = filters.search !== lastSearchRef.current;
    lastSearchRef.current = filters.search;
    const wait = searchChanged ? 300 : 0;
    const handle = setTimeout(async () => {
      setLoading(true);
      setError('');
      try {
        const q = buildQuery();
        const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
        const listParams = new URLSearchParams({ ...q, page: 1, page_size: 200 }).toString();
        const statsParams = new URLSearchParams(q).toString();
        const [listResp, statsResp] = await Promise.all([
          fetch(`/api${config.listEndpoint}?${listParams}`, { headers }),
          fetch(`/api${config.statsEndpoint}?${statsParams}`, { headers }),
        ]);
        // Treat 429 as a real error so the user gets the actual message
        // ("Too many requests, slow down") instead of an empty table.
        if (listResp.status === 429 || statsResp.status === 429) {
          throw new Error('Too many requests — slow down and try again in a moment.');
        }
        const listRes = await listResp.json().catch(() => null);
        const statsRes = await statsResp.json().catch(() => null);
        setRows(Array.isArray(listRes?.data) ? listRes.data : []);
        setStats(statsRes || null);
      } catch (err) {
        setError(err.message || 'Failed to load');
      }
      setLoading(false);
    }, wait);
    return () => clearTimeout(handle);
  }, [buildQuery, config.listEndpoint, config.statsEndpoint, filters.search, reloadKey]);

  function handlePreset(p) {
    setActivePreset(p);
    setFilters(f => {
      if (p === 'today') return { ...f, date_from: getTodayCT(), date_to: getTodayCT() };
      if (p === '7days') return { ...f, date_from: getLast7DaysCT(), date_to: getTodayCT() };
      if (p === 'month') return { ...f, date_from: getThisMonthStartCT(), date_to: getTodayCT() };
      if (p === 'custom') return { ...f, date_from: '', date_to: '' };
      return f;
    });
  }

  async function handleExport() {
    setExporting(true);
    try {
      const params = new URLSearchParams(buildQuery()).toString();
      const res = await fetch(`/api${config.exportEndpoint}?${params}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) throw new Error('Export failed');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${config.key}-${new Date().toISOString().split('T')[0]}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Export failed');
    }
    setExporting(false);
  }

  // KPI values — config supplies the (key, label, color) per slot; we just
  // pluck from the stats payload. Money slots format via formatMoney.
  const statsCards = useMemo(() => {
    if (!stats) return [];
    return config.kpis.map(k => {
      const raw = stats[k.key];
      const value = k.money
        ? formatMoney(raw)
        : (raw ?? 0);
      return { label: k.label, value, color: k.color };
    });
  }, [stats, config.kpis]);

  const scope = stats?.scope_kind ? SCOPE_PALETTES[stats.scope_kind] : null;

  const hasActiveFilters =
    filters.truck_id || filters.status || filters.severity ||
    filters.priority || filters.assigned_to || filters.driver_id ||
    filters.reported_by || filters.search;

  function clearFilters() {
    setFilters({
      truck_id: '', status: '', severity: '', priority: '',
      assigned_to: '', driver_id: '', reported_by: '', search: '',
      date_from: getThisMonthStartCT(), date_to: getTodayCT(),
    });
    setActivePreset('month');
  }

  return (
    <div className="history-page">
      <button onClick={() => navigate('/maintenance')}
        style={{ background: 'none', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', fontSize: 13, marginBottom: 12, padding: 0 }}>
        ← Back to Truck Maintenance
      </button>

      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ display: 'flex', alignItems: 'center', gap: 10, margin: 0, flexWrap: 'wrap' }}>
          {config.icon && <span style={{ fontSize: 24 }}>{config.icon}</span>}
          <span>{config.title}</span>
          {scope && (
            <span style={{
              fontSize: 11, fontWeight: 700, letterSpacing: 0.4,
              padding: '4px 10px', borderRadius: 999,
              background: scope.bg, color: scope.fg, textTransform: 'uppercase',
            }}>{scope.label}</span>
          )}
        </h2>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {(config.SubmitModal || config.submitTo) && (!config.submitGate || can(config.submitGate)) && (
            <button className="btn btn-gold" onClick={() => {
              if (config.SubmitModal) return setShowSubmitModal(true);
              if (typeof config.submitTo === 'function') return config.submitTo(navigate);
              navigate(config.submitTo);
            }} style={{ fontWeight: 700 }}>
              + {config.submitLabel || `Submit new ${config.title}`}
            </button>
          )}
          <button type="button" className="btn btn-gold" onClick={handleExport}
            disabled={exporting || rows.length === 0}>
            {exporting ? 'Exporting…' : 'Export Excel'}
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 }}>
        {(statsCards.length ? statsCards : config.kpis.map(k => ({ label: k.label, value: '—', color: k.color }))).map(s => (
          <div key={s.label} style={{
            background: '#fff', borderRadius: 12, padding: '14px 16px',
            border: '1px solid #e2e8f0', textAlign: 'center',
          }}>
            <div style={{ fontSize: 20, fontWeight: 800, color: s.color, fontVariantNumeric: 'tabular-nums' }}>{s.value}</div>
            <div style={{ fontSize: 11, color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filter section */}
      <div className="search-filters" style={{ marginBottom: 16 }}>
        <div className="date-preset-row" style={{ display: 'flex', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
          {['today', '7days', 'month', 'custom'].map(p => (
            <button key={p} type="button"
              className={`btn btn-sm ${activePreset === p ? 'btn-primary' : 'btn-muted'}`}
              onClick={() => handlePreset(p)}>
              {p === 'today' ? 'Today' : p === '7days' ? 'Last 7 Days' : p === 'month' ? 'This Month' : 'Custom'}
            </button>
          ))}
        </div>
        {activePreset === 'custom' && (
          <div className="date-range-row" style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
            <label>From</label>
            <input type="date" value={filters.date_from}
              onChange={e => setFilters({ ...filters, date_from: e.target.value })} />
            <label>To</label>
            <input type="date" value={filters.date_to}
              onChange={e => setFilters({ ...filters, date_to: e.target.value })} />
          </div>
        )}

        <div className="filter-row" style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8,
        }}>
          {config.filters.includes('truck_id') && (
            <select value={filters.truck_id} onChange={e => setFilters({ ...filters, truck_id: e.target.value })}>
              <option value="">All Trucks</option>
              {trucks.map(t => <option key={t.id} value={t.id}>#{t.unit_number}</option>)}
            </select>
          )}
          {config.filters.includes('status') && (
            <select value={filters.status} onChange={e => setFilters({ ...filters, status: e.target.value })}>
              <option value="">All Statuses</option>
              {(config.statusOptions || []).map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          )}
          {config.filters.includes('severity') && (
            <select value={filters.severity} onChange={e => setFilters({ ...filters, severity: e.target.value })}>
              <option value="">All Severity</option>
              <option value="minor">Minor</option>
              <option value="moderate">Moderate</option>
              <option value="major">Major</option>
              <option value="critical">Critical</option>
            </select>
          )}
          {config.filters.includes('priority') && (
            <select value={filters.priority} onChange={e => setFilters({ ...filters, priority: e.target.value })}>
              <option value="">All Priorities</option>
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
              <option value="urgent">Urgent</option>
            </select>
          )}
          {config.filters.includes('assigned_to') && (
            <select value={filters.assigned_to} onChange={e => setFilters({ ...filters, assigned_to: e.target.value })}>
              <option value="">Assigned To</option>
              {activeUsers.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
          )}
          {config.filters.includes('driver_id') && (
            <select value={filters.driver_id} onChange={e => setFilters({ ...filters, driver_id: e.target.value })}>
              <option value="">All Drivers</option>
              {activeUsers.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
          )}
          {config.filters.includes('reported_by') && (
            <select value={filters.reported_by} onChange={e => setFilters({ ...filters, reported_by: e.target.value })}>
              <option value="">Reported By</option>
              {activeUsers.map(u => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
          )}
          {config.filters.includes('search') && (
            <input placeholder={config.searchPlaceholder || 'Search…'} value={filters.search}
              onChange={e => setFilters({ ...filters, search: e.target.value })} />
          )}
          {hasActiveFilters && (
            <button type="button" onClick={clearFilters}
              style={{ fontSize: 11, color: '#6d28d9', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>
              Clear Filters
            </button>
          )}
          {loading && <span style={{ fontSize: 11, color: '#94a3b8', fontStyle: 'italic', alignSelf: 'center' }}>Loading…</span>}
        </div>
      </div>

      {error && (
        <div style={{ padding: '12px 16px', background: '#fee', border: '1px solid #c00', borderRadius: 8, color: '#900', marginBottom: 16, fontWeight: 600 }}>
          {error}
        </div>
      )}

      {/* Table */}
      {loading && rows.length === 0 ? (
        <p style={{ color: 'var(--text-muted)' }}>Loading…</p>
      ) : rows.length === 0 ? (
        <div style={{
          padding: 40, textAlign: 'center', color: 'var(--text-muted)',
          background: '#fff', borderRadius: 8, border: '1px dashed var(--border-subtle)',
        }}>
          No {config.title.toLowerCase()} match these filters.
        </div>
      ) : (
        <div className="table-wrapper">
          <table className="data-table">
            <thead>
              <tr>
                {config.columns.map(col => (
                  <th key={col.key} style={col.thStyle || (col.right ? { textAlign: 'right' } : undefined)}>
                    {col.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r.id} style={config.rowHref ? { cursor: 'pointer' } : undefined}
                  onClick={() => config.rowHref && navigate(config.rowHref(r))}>
                  {config.columns.map(col => (
                    <td key={col.key} style={col.tdStyle || (col.right ? { textAlign: 'right' } : undefined)}>
                      {col.render ? col.render(r) : (r[col.key] ?? '—')}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* In-place submit modal (e.g. Work Orders create) */}
      {showSubmitModal && config.SubmitModal && (
        <config.SubmitModal
          onClose={() => setShowSubmitModal(false)}
          onCreated={() => { setShowSubmitModal(false); setReloadKey(k => k + 1); }}
        />
      )}
    </div>
  );
}

// Re-exports — utils used by the per-bucket config files so each config
// stays focused on its own shape rather than re-declaring helpers.
export { fmtDate, formatMoney };
